import React, { useState } from 'react';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';

interface Props {
  show: boolean;
  brokerName: string;
  totalLoans: number;
  totalCommission: number;
  hideCommissions: boolean;
  onConfirm: () => Promise<void>;
  onDeny: () => void;
}

const BrokerDataAttestationModal: React.FC<Props> = ({
  show,
  brokerName,
  totalLoans,
  totalCommission,
  hideCommissions,
  onConfirm,
  onDeny,
}) => {
  const [confirmed1, setConfirmed1] = useState(false);
  const [confirmed2, setConfirmed2] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!confirmed1 || !confirmed2) {
      toast.error('Please confirm both checkboxes');
      return;
    }

    setSubmitting(true);
    try {
      await onConfirm();
    } catch (err: any) {
      toast.error('Failed to confirm: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Confirm Your Identity" show={show} onClose={() => {}}>
      <div className="space-y-4">
        <div className="bg-blue-900 border border-blue-700 rounded-md p-4">
          <p className="text-blue-200 text-sm">
            We've linked your account to commission data for:
          </p>
        </div>

        <div className="bg-gray-700 rounded-md p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-400">Broker Name:</span>
            <span className="text-white font-semibold">{brokerName}</span>
          </div>
          {!hideCommissions && (
            <div className="flex justify-between">
              <span className="text-gray-400">Active Loans:</span>
              <span className="text-white font-semibold">{totalLoans}</span>
            </div>
          )}
          {!hideCommissions && (
            <div className="flex justify-between">
              <span className="text-gray-400">Total Commission (YTD):</span>
              <span className="text-white font-semibold">
                ${(totalCommission / 100).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>

        <div className="bg-red-900 border border-red-700 rounded-md p-4">
          <p className="text-red-200 text-sm font-semibold mb-2">Important Legal Notice</p>
          <p className="text-red-200 text-xs">
            You are certifying that this is YOUR commission data. Accessing another broker's data
            is a serious violation of privacy law (Australian Privacy Act APP 13) and may result in:
          </p>
          <ul className="text-red-200 text-xs mt-2 ml-4 list-disc space-y-1">
            <li>Immediate account termination</li>
            <li>Civil liability for privacy breach</li>
            <li>Criminal prosecution for fraud</li>
            <li>Report to ASIC and/or OAIC</li>
          </ul>
        </div>

        <div className="space-y-3">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed1}
              onChange={(e) => setConfirmed1(e.target.checked)}
              className="mt-1 w-4 h-4"
            />
            <span className="ml-3 text-sm text-gray-300">
              I confirm this is my commission data and I am {brokerName}
            </span>
          </label>

          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed2}
              onChange={(e) => setConfirmed2(e.target.checked)}
              className="mt-1 w-4 h-4"
            />
            <span className="ml-3 text-sm text-gray-300">
              I understand that accessing another broker's data is illegal and may result in
              prosecution under Australian privacy and fraud laws
            </span>
          </label>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            onClick={handleConfirm}
            disabled={!confirmed1 || !confirmed2 || submitting}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Processing...' : 'Confirm - This Is My Data'}
          </button>
          <button
            onClick={onDeny}
            disabled={submitting}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg"
          >
            This Is Not Me
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BrokerDataAttestationModal;
